import { useForm } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import Button from "@/components/Button/Button";
import formRenderer from "@/lib/formRenderer";
import type { FormConfig } from "@/lib/formRenderer";
import { VALIDATION_ERROR_MESSAGES } from "@/lib/constants";
import { emailRegex } from "@/lib/regex";
import toast from "@/lib/toast";
import {
  combineClasses,
  requiredMessage,
  saveRefreshToken,
  saveToken,
  captalizeEachWord,
} from "@/lib/utils";
import routes from "@/routes/routes";
import AuthService from "@/services/api/auth";
import styles from "./Login.module.scss";
import {
  continueText,
  emailId,
  emailPlaceholder,
  forgotLinkText,
  heading,
  keepSignedIn,
  password,
  passwordPlaceholder,
  signing,
  subHeading,
  type LoginFormValues,
} from "@/services/api/login";

// Structurally identical fields (one input, one label, one error) are
// config-driven via `formRenderer` instead of hand-written `<FormControl>`
// blocks — see docs/decisions.md #26. `autoComplete` is set explicitly
// per field rather than left to formRenderer's "new-password" default,
// which is meant for signup/change-password forms, not sign-in.
const LOGIN_FIELDS: FormConfig = {
  email: {
    controlType: "input",
    label: emailId.toUpperCase(),
    placeholder: emailPlaceholder,
    isRequired: true,
    autoComplete: "username",
    validations: {
      required: requiredMessage("Email ID"),
      pattern: {
        value: emailRegex,
        message: VALIDATION_ERROR_MESSAGES.EMAIL_FORMAT,
      },
    },
    props: { className: styles.loginInput },
  },
  password: {
    controlType: "password",
    label: password.toUpperCase(),
    placeholder: passwordPlaceholder,
    isRequired: true,
    autoComplete: "current-password",
    validations: { required: requiredMessage("Password") },
    props: { className: styles.loginInput },
  },
};

const REMEMBER_FIELD: FormConfig = {
  remember: {
    controlType: "checkbox",
    label: keepSignedIn,
  },
};

const Login = () => {
  const navigate = useNavigate();
  const { register, handleSubmit, formState } = useForm<LoginFormValues>({
    defaultValues: { email: "", password: "", remember: false },
    mode: "onTouched",
  });
  const { isValid, isSubmitting } = formState;

  const login = async (values: LoginFormValues) => {
    try {
      const response = await AuthService.login({
        email: values.email,
        password: values.password,
      });

      if (!response?.success) {
        throw new Error(response?.message || "Login failed");
      }

      saveToken(response.data.accessToken);
      saveRefreshToken(response.data.refreshToken);

      localStorage.setItem(
        "user",
        JSON.stringify({
          email: values.email,
          permissions: response.data.permissions,
        }),
      );

      navigate(routes.privateRoutes.DASHBOARD.path);

      return response;
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } }; message?: string })?.response
          ?.data?.message ||
        (error as Error)?.message ||
        "Login failed";

      throw new Error(message);
    }
  };

  const onSubmit: SubmitHandler<LoginFormValues> = async (values) => {
    await toast.promise(login(values), {
      loading: "Logging in...",
      success: () => "Login Successful",
      error: (error) => (error as Error).message,
    });
  };

  return (
    <div className={styles.mainDiv}>
      <p className={styles.heading}>{captalizeEachWord(heading)}</p>
      <p className={styles.subHeading}>{subHeading}</p>

      <form onSubmit={handleSubmit(onSubmit)} className={styles.form} autoComplete="off">
        <div className={styles.formControls}>
          {formRenderer<LoginFormValues>(LOGIN_FIELDS, formState, register)}

          <div className={styles.rememberField}>
            {formRenderer<LoginFormValues>(REMEMBER_FIELD, formState, register)}

            <Link
              className={combineClasses(styles.bottomText, "link-text")}
              to={routes.publicRoutes.RESET_PASSWORD.path}
            >
              {forgotLinkText.toUpperCase()}
            </Link>
          </div>
        </div>

        <Button
          type="submit"
          className={combineClasses(styles.button)}
          large
          disabled={isSubmitting || !isValid}
        >
          {isSubmitting ? captalizeEachWord(signing) : continueText.toUpperCase()}
        </Button>
      </form>
    </div>
  );
};

export default Login;
